import React, { useCallback, useEffect, useRef } from 'react';
import classNameBind from 'classnames/bind';
import { createPortal } from 'react-dom';

import styles from './ModalView.module.css';
import { ModalViewHeader } from './components/ModalViewHeader';
import { KEYS } from '../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);
const FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const focusTrap = (event, ref) => {
  if (event.key === KEYS.TAB) {
    const modalElement = ref.current;
    const focusableElements = Array.from(
      modalElement.querySelectorAll(FOCUSABLE_SELECTORS),
    ).filter(el => !Array.from(el.classList).some(className => className.includes('disabled')));

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (
      !event.shiftKey
          && document.activeElement === lastElement
    ) {
      event.preventDefault();
      firstElement.focus();
    }
  }
};

const ModalView = ({
  children,
  actions = null,
  wide = false,
  title,
  onClose = () => {},
  dataId = 'ModalView',
}) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    const previousActiveElement = document.activeElement;

    return () => { previousActiveElement.focus(); };
  }, []);

  useEffect(() => {
    const modal = overlayRef.current;

    if (modal === null) {
      return;
    }

    if (document.activeElement === modal || modal.contains(document.activeElement)) {
      return;
    }

    modal.focus();
  });

  const handleKeyDown = useCallback((event) => {
    if (event.key === KEYS.ESC) {
      onClose();
    }

    focusTrap(event, overlayRef);
  }, [onClose]);

  const handleMouseDown = (mouseDownEvent) => {
    document.addEventListener('mouseup', (mouseUpEvent) => {
      if (mouseDownEvent.target === overlayRef.current && mouseUpEvent.target === overlayRef.current) {
        onClose();
      }
    }, { once: true });
  };

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a modal is interactive
    <div
      className={bindClasses('overlay')}
      tabIndex="-1"
      role="dialog"
      data-ppuc-id={dataId}
      ref={overlayRef}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
    >
      <div
        className={bindClasses('container', { wide })}
        data-ppuc-id={`${dataId}.container`}
      >
        <ModalViewHeader
          title={title}
          onClose={actions ? undefined : onClose}
          dataId={`${dataId}.header`}
        />
        <div className={bindClasses('body')} data-ppuc-id={`${dataId}.body`}>
          {children}
        </div>
        {actions && (
          <div className={bindClasses('actions')} data-ppuc-id={`${dataId}.actions`}>
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export { ModalView };
